import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  Card,
  Row,
  Col,
  InputGroup,
  Button,
  Table,
  Form
} from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// components
import Flex from 'components/common/Flex';
import ActionButton from 'components/common/ActionButton';
// types
import { USE_YN_SELECT, ACTION_TYPE } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
// state
import { useConfirmModalState } from 'global/confirmModalState';
// function
import { getLoginInform } from 'function/login';
// api
import { factory } from 'api/haccp/commonApi';
import {
  processInfoDetail,
  processInfoCreate,
  processInfoUpdate,
  procsCodeCheck,
  requestCodeCheck
} from 'api/haccp/standardInfo/processInfoApi';

/**
 * @description 공정검사의뢰코드관리 상세 및 수정페이지
 */
const Detail = () => {
  /* router info*/
  const history = useHistory();
  const { state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  const [adminYn, setAdminYn] = useState(null); // 'Y': 관리자, N: 이천,군산 담당자
  const [action, setAction] = useState('');
  const [actionType, setActionType] = useState('');

  const [validated, setValidated] = useState(false);
  const [factoryOptions, setFactoryOptions] = useState(null);
  const [useYnOptions] = useState(USE_YN_SELECT);

  // 공정정보
  const [processInfo, setProcessInfo] = useState({
    pltCd: '', // 공장코드
    procsCd: null, // 공정코드
    procsCdCheck: null, // 공정코드 중복체크
    procsNm: null, // 공정명
    useYn: 'Y', // 사용유무
    description: null, // 비고
    isDisabled: false
  });

  // 공정상세정보
  const [processDetail, setProcessDetail] = useState([]);

  /* event */

  /* 공정검사의뢰코드 목록페이지 */
  const onList = () => {
    history.push({
      pathname: '/haccp/processInfo',
      state: state
    });
  };

  /* 공정코드 중복확인 */
  const processCodeCheck = async e => {
    const { name } = e.target || {};
    const { procsCd } = processInfo || {};

    if (procsCd === null) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '공정코드를 입력해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    const requestBody = {
      [name]: processInfo.procsCd
    };

    const response = await procsCodeCheck(requestBody);

    const { code, data } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      return;
    }

    const { duplicateYn } = data || {};

    if (duplicateYn === 'Y') {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '이미 등록된 공정코드 입니다.',
        isConfirmBtn: true
      });

      setProcessInfo({ ...processInfo, procsCdCheck: false });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: '사용가능한 공정코드 입니다.',
      isConfirmBtn: true
    });

    setProcessInfo({ ...processInfo, procsCdCheck: true });
  };

  /* 의뢰명코드 중복확인 */
  const procsInspReqCodeCheck = async (index, requestCd) => {
    const requestBody = {
      procsInspReqCd: requestCd
    };

    const response = await requestCodeCheck(requestBody);

    const { code, data } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      return;
    }

    const { duplicateYn } = data || {};

    let newList = [...processDetail];

    if (duplicateYn === 'Y') {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '이미 등록된 의뢰명 코드 입니다.',
        isConfirmBtn: true
      });

      newList[index] = { ...newList[index], procsInspReqCdCheck: false };
      setProcessDetail(newList);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: '사용가능한 의뢰명 코드입니다.',
      isConfirmBtn: true
    });
    newList[index] = { ...newList[index], procsInspReqCdCheck: true };
    setProcessDetail(newList);
  };

  /* 공정상세정보 data update */
  const updateFieldChanged = (e, index) => {
    const { name, value } = e.target || {};
    const newList = [...processDetail];

    // 의뢰명코드 input Data 변경 시 중복검사 초기화
    if (name === 'procsInspReqCd') {
      newList[index] = {
        ...newList[index],
        [name]: value,
        procsInspReqCdCheck: null
      };
      setProcessDetail(newList);
      return;
    }

    newList[index] = { ...newList[index], [name]: value };

    setProcessDetail(newList);
  };

  /* 등록 및 상세 */
  const handleSubmit = event => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    const { procsCdCheck } = processInfo || {};

    if (procsCdCheck === null || procsCdCheck === false) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '공정코드 중복확인을 해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    const isDuplicateCheck = processDetail?.some(
      e => e.procsInspReqCdCheck === null || e.procsInspReqCdCheck === false
    );

    if (isDuplicateCheck === true) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '의뢰명코드 중복확인을 해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    if (processDetail.length === 0) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '공정상세정보를 등록해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `공정검사 의뢰코드를 ${action} 하시겠습니까?`,
      isCancelBtn: true,
      isConfirmBtn: true,
      onConfirm: () => {
        onSubmit();
      }
    });
  };

  const onSubmit = async () => {
    const { pltCd, procsCd, procsNm, useYn, description } = processInfo || {};

    let requestBody = {};
    let response = null;

    if (actionType === 'create') {
      // 등록
      requestBody = {
        pltCd: pltCd,
        procsCd: procsCd,
        procsNm: procsNm,
        useYn: useYn,
        description: description,
        procsInspReqList: processDetail?.map(e => ({
          procsInspReqCd: e.procsInspReqCd,
          procsInspReqNm: e.procsInspReqNm,
          useYn: e.useYn,
          description: e.description
        }))
      };

      response = await processInfoCreate(requestBody);
    } else {
      // 수정
      requestBody = {
        useYn: useYn,
        description: description,
        procsInspReqList: processDetail?.map(e => ({
          seq: e.seq !== undefined ? e.seq : undefined,
          procsInspReqCd: e.seq === undefined ? e.procsInspReqCd : undefined,
          procsInspReqNm: e.seq === undefined ? e.procsInspReqNm : undefined,
          useYn: e.useYn,
          description: e.description
        }))
      };

      response = await processInfoUpdate(seq, requestBody);
    }

    const { code, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `공정검사 의뢰코드가 ${action} 되었습니다.`,
      isConfirmBtn: true
    });

    // 목록페이지 이동
    onList();
  };

  /* 공정 상세정보 추가 */
  const detailInfoAdd = () => {
    const detailInfo = {
      procsInspReqCd: '', // 공정검사의뢰코드
      procsInspReqCdCheck: null, // 공정검사의뢰코드  중복체크
      procsInspReqNm: '', // 공정검사의뢰명
      useYn: 'Y',
      description: '',
      isDisabled: false
    };

    const newList = [...processDetail, detailInfo];
    setProcessDetail(newList);
  };

  /* 공정 상세정보 삭제 */
  const detailInfoDelete = index => {
    if (index === 0) return;

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: '공장상세정보를 삭제하시겠습니까?',
      isConfirmBtn: true,
      isCancelBtn: true,
      onConfirm: () => {
        const newList = [...processDetail];
        newList.splice(index, 1);
        setProcessDetail(newList);
      }
    });
  };

  /* 공장목록 API */
  const factoryList = async () => {
    const response = await factory();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    setFactoryOptions(list);
  };

  /* 공정검사의뢰코드 상세 API */
  const onDetail = async () => {
    const response = await processInfoDetail(seq);

    const { code, data, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    const { pltCd, procsCd, procsNm, useYn, description, procsInspReqList } =
      data || {};

    // 공정정보
    setProcessInfo({
      ...processInfo,
      pltCd: pltCd,
      procsCd: procsCd,
      procsCdCheck: true,
      procsNm: procsNm,
      useYn: useYn,
      description: description,
      isDisabled: true
    });

    // 공정 상세 정보
    setProcessDetail(
      procsInspReqList?.map(e => ({
        ...e,
        isDisabled: true,
        procsInspReqCdCheck: true
      })) ?? []
    );
  };

  /* 로그인 정보 */
  const loginInfo = () => {
    const { adminYn, pltCd } = getLoginInform();

    if (adminYn === 'Y') {
      setAdminYn(adminYn);
      return;
    }

    setProcessInfo(prevState => ({
      ...prevState,
      pltCd: pltCd
    }));

    setAdminYn(adminYn);
  };

  /* watch */
  useEffect(() => {
    // 공장 API 호출
    factoryList();
    // 로그인 정보
    loginInfo();
  }, []);

  useEffect(() => {
    switch (actionType) {
      case ACTION_TYPE.CREATE: {
        setAction('등록');
        break;
      }
      case ACTION_TYPE.UPDATE: {
        setAction('수정');
        onDetail();
        break;
      }
    }
  }, [actionType]);

  useLayoutEffect(() => {
    if (seq) {
      setActionType('update');
      return;
    }
    setActionType('create');
  }, []);

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="align-items-center g-2">
              <Col>
                <h5 className="mb-0">공정 검사의뢰코드 {action}</h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="bg-light">
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*공장</Form.Label>
                  <Form.Select
                    required
                    size="sm"
                    onChange={e =>
                      setProcessInfo({ ...processInfo, pltCd: e.target.value })
                    }
                    value={processInfo.pltCd}
                    disabled={processInfo.isDisabled || adminYn === 'N'}
                  >
                    <option hidden value="">
                      공장을 선택해주세요.
                    </option>
                    {factoryOptions?.map(option => (
                      <option key={option.pltCd} value={option.pltCd}>
                        {option.pltNmKo}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*공정 코드</Form.Label>
                  <InputGroup>
                    <Form.Control
                      required
                      type="text"
                      size="sm"
                      placeholder="20자 이내로 입력해주세요."
                      value={processInfo.procsCd || ''}
                      onChange={e =>
                        setProcessInfo({
                          ...processInfo,
                          procsCd: e.target.value,
                          procsCdCheck: null
                        })
                      }
                      disabled={processInfo.isDisabled}
                    />
                    {processInfo.isDisabled === false && (
                      <Button
                        variant="success"
                        size="sm"
                        name="procsCd"
                        disabled={processInfo.procsCd === ''}
                        onClick={e => processCodeCheck(e)}
                      >
                        중복확인
                      </Button>
                    )}
                    {processInfo.isDisabled === false &&
                      processInfo.procsCdCheck === true && (
                        <div className="text-primary d-flex align-items-center ms-2 fw-bold">
                          사용가능
                        </div>
                      )}
                    {processInfo.isDisabled === false &&
                      processInfo.procsCdCheck === false && (
                        <div className="text-danger d-flex align-items-center ms-2 fw-bold">
                          중복코드
                        </div>
                      )}
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*공정명</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    size="sm"
                    placeholder="20자 이내로 입력해주세요."
                    value={processInfo.procsNm || ''}
                    onChange={e =>
                      setProcessInfo({
                        ...processInfo,
                        procsNm: e.target.value
                      })
                    }
                    disabled={processInfo.isDisabled}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>비고</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="30자 이내로 입력해주세요."
                    maxLength="30"
                    size="sm"
                    value={processInfo.description || ''}
                    onChange={e =>
                      setProcessInfo({
                        ...processInfo,
                        description: e.target.value
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*사용유무</Form.Label>
                  <Flex>
                    <Form.Check
                      type="radio"
                      id="use"
                      label="사용"
                      name="useYn"
                      value="Y"
                      checked={processInfo.useYn === 'Y'}
                      onChange={e =>
                        setProcessInfo({
                          ...processInfo,
                          useYn: e.target.value
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      id="unuse"
                      label="미사용"
                      className="ms-3"
                      name="useYn"
                      value="N"
                      checked={processInfo.useYn === 'N'}
                      onChange={e =>
                        setProcessInfo({
                          ...processInfo,
                          useYn: e.target.value
                        })
                      }
                    />
                  </Flex>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Row className="align-items-center g-2">
              <Col>
                <h5 className="mb-0">공정 상세 정보</h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="pt-0">
            <Table responsive bordered className="fs--1 text-center mb-0">
              <thead className="bg-200 text-700 align-middle border-300">
                <tr>
                  <th>*의뢰명 코드</th>
                  <th>*의뢰명</th>
                  <th>비고</th>
                  <th>*사용유무</th>
                  <th>
                    <Button size="sm" onClick={detailInfoAdd}>
                      <FontAwesomeIcon
                        icon="plus"
                        className="text-white align-middle"
                      />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {processDetail?.map(
                  (
                    {
                      procsInspReqCd,
                      procsInspReqNm,
                      useYn,
                      description,
                      isDisabled,
                      procsInspReqCdCheck
                    },
                    index
                  ) => (
                    <tr key={index}>
                      <td>
                        <InputGroup>
                          <Form.Control
                            required
                            type="text"
                            placeholder="20자 이내로 입력해주세요."
                            maxLength="20"
                            size="sm"
                            name="procsInspReqCd"
                            value={procsInspReqCd || ''}
                            disabled={isDisabled}
                            onChange={e => updateFieldChanged(e, index)}
                          />
                          {isDisabled === false && (
                            <Button
                              variant="success"
                              size="sm"
                              name="procsInspReqCd"
                              disabled={procsInspReqCd === ''}
                              onClick={() =>
                                procsInspReqCodeCheck(index, procsInspReqCd)
                              }
                            >
                              중복확인
                            </Button>
                          )}
                          {isDisabled === false &&
                            procsInspReqCdCheck === true && (
                              <div className="text-primary d-flex align-items-center ms-2 fw-bold">
                                사용가능
                              </div>
                            )}
                          {isDisabled === false &&
                            procsInspReqCdCheck === false && (
                              <div className="text-danger d-flex align-items-center ms-2 fw-bold">
                                중복코드
                              </div>
                            )}
                        </InputGroup>
                      </td>
                      <td>
                        <Form.Control
                          required
                          type="text"
                          placeholder="20자 이내로 입력해주세요."
                          maxLength="20"
                          size="sm"
                          name="procsInspReqNm"
                          value={procsInspReqNm || ''}
                          disabled={isDisabled}
                          onChange={e => updateFieldChanged(e, index)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="30자 이내로 입력해주세요."
                          maxLength="30"
                          size="sm"
                          name="description"
                          value={description || ''}
                          onChange={e => updateFieldChanged(e, index)}
                        />
                      </td>
                      <td>
                        <Flex justifyContent="center">
                          <Form.Select
                            size="sm"
                            name="useYn"
                            value={useYn}
                            onChange={e => updateFieldChanged(e, index)}
                          >
                            {useYnOptions?.map(option => (
                              <option key={option.key} value={option.key}>
                                {option.value}
                              </option>
                            ))}
                          </Form.Select>
                        </Flex>
                      </td>
                      <td>
                        {isDisabled === false && (
                          <ActionButton
                            icon="trash-alt"
                            title="삭제"
                            variant="action"
                            className="p-0 lh-1 text-danger"
                            onClick={() => detailInfoDelete(index)}
                          />
                        )}
                      </td>
                    </tr>
                  )
                )}
                {processDetail?.length < 1 && (
                  <tr>
                    <td colSpan={5} className="w-auto">
                      공정 상세 정보가 존재하지 않습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
          <Card.Footer className="d-flex align-items-center">
            <Button
              size="sm"
              variant="secondary"
              className="ms-auto"
              onClick={onList}
            >
              취소
            </Button>
            <Button size="sm" className="ms-2" type="submit">
              {action}
            </Button>
          </Card.Footer>
        </Card>
      </Form>
    </>
  );
};

export default Detail;
